import { useEffect, useState } from 'react'
import { useAuthStore } from '../store/auth'
import { usePageHeader } from '../hooks/usePageHeader'
import {
  listAdminUsers, setUserTier, grantSudo, revokeSudo,
  getAdminConfig, updateAdminConfig,
} from '../api/admin'
import type { AdminUser } from '../api/admin'
import { getGlobalTheme, setGlobalTheme } from '../api/schemes'
import { SchemePicker } from '../components/SchemePicker'
import type { ColorTheme } from '../components/Entry/colorTheme'

function formatBytes(mb: number): string {
  if (mb < 1024) return `${mb} mb`
  return `${(mb / 1024).toFixed(1)} gb`
}

export function Admin() {
  const { isSudo, username } = useAuthStore()

  // Users
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [usersError, setUsersError] = useState('')
  const [rowSaving, setRowSaving] = useState<Record<string, boolean>>({})
  const [rowError, setRowError] = useState<Record<string, string>>({})

  // Config
  const [randoMb, setRandoMb] = useState('')
  const [budMb, setBudMb] = useState('')
  const [bffMb, setBffMb] = useState('')
  const [defaultTier, setDefaultTier] = useState('rando')
  const [configSaving, setConfigSaving] = useState(false)
  const [configSaved, setConfigSaved] = useState(false)
  const [configError, setConfigError] = useState('')

  // Global theme
  const [globalThemeRaw, setGlobalThemeRaw] = useState<ColorTheme | null>(null)
  const [globalThemeLoaded, setGlobalThemeLoaded] = useState(false)
  const [themeSaving, setThemeSaving] = useState(false)
  const [themeSaved, setThemeSaved] = useState(false)
  const [themeError, setThemeError] = useState('')

  useEffect(() => {
    if (!isSudo) return
    listAdminUsers()
      .then(setUsers)
      .catch(() => setUsersError('failed to load users'))
    getAdminConfig()
      .then((cfg) => {
        setRandoMb(cfg.quota_rando_mb ?? '100')
        setBudMb(cfg.quota_bud_mb ?? '1024')
        setBffMb(cfg.quota_bff_mb ?? '10240')
        setDefaultTier(cfg.default_tier ?? 'rando')
      })
      .catch(() => {})
    getGlobalTheme().then((t) => { setGlobalThemeRaw(t); setGlobalThemeLoaded(true) }).catch(() => { setGlobalThemeLoaded(true) })
  }, [isSudo, username])

  usePageHeader('admin')

  if (!isSudo) return null

  async function handleTierChange(u: AdminUser, tier: string) {
    setRowSaving((s) => ({ ...s, [u.username]: true }))
    setRowError((e) => ({ ...e, [u.username]: '' }))
    try {
      await setUserTier(u.username, tier)
      setUsers((prev) => prev?.map((x) => x.username === u.username ? { ...x, storage_tier: tier } : x) ?? prev)
    } catch (err) {
      setRowError((e) => ({ ...e, [u.username]: err instanceof Error ? err.message : 'failed' }))
    } finally {
      setRowSaving((s) => ({ ...s, [u.username]: false }))
    }
  }

  async function handleSudoChange(u: AdminUser, newValue: boolean) {
    setRowSaving((s) => ({ ...s, [u.username]: true }))
    setRowError((e) => ({ ...e, [u.username]: '' }))
    try {
      if (newValue) {
        await grantSudo(u.username)
      } else {
        await revokeSudo(u.username)
      }
      setUsers((prev) => prev?.map((x) => x.username === u.username ? { ...x, is_sudo: newValue } : x) ?? prev)
    } catch (err) {
      setRowError((e) => ({ ...e, [u.username]: err instanceof Error ? err.message : 'failed' }))
    } finally {
      setRowSaving((s) => ({ ...s, [u.username]: false }))
    }
  }

  async function handleSaveConfig() {
    setConfigSaving(true)
    setConfigError('')
    setConfigSaved(false)
    try {
      await updateAdminConfig({
        rando_mb: randoMb ? parseInt(randoMb, 10) : undefined,
        bud_mb: budMb ? parseInt(budMb, 10) : undefined,
        bff_mb: bffMb ? parseInt(bffMb, 10) : undefined,
        default_tier: defaultTier || undefined,
      })
      setConfigSaved(true)
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'failed to save')
    } finally {
      setConfigSaving(false)
    }
  }

  async function handleSaveGlobalTheme() {
    if (!globalThemeRaw) return
    setThemeSaving(true)
    setThemeError('')
    setThemeSaved(false)
    try {
      await setGlobalTheme(globalThemeRaw)
      setThemeSaved(true)
    } catch (err) {
      setThemeError(err instanceof Error ? err.message : 'failed to save')
    } finally {
      setThemeSaving(false)
    }
  }

  return (
    <div>

      <section style={{ marginBottom: '2.5rem' }}>
        <h3>users</h3>
        {usersError && <p style={{ color: 'var(--color-1)', fontSize: '0.85rem' }}>{usersError}</p>}
        {users === null && !usersError && <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>loading…</p>}
        {users !== null && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-fg)', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.75rem 0.4rem 0' }}>username</th>
                  <th style={{ padding: '0.4rem 0.75rem' }}>email</th>
                  <th style={{ padding: '0.4rem 0.75rem' }}>tier</th>
                  <th style={{ padding: '0.4rem 0.75rem' }}>storage used</th>
                  <th style={{ padding: '0.4rem 0.75rem' }}>sudo</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.username} style={{ borderBottom: '1px solid color-mix(in srgb, var(--color-fg) 20%, transparent)' }}>
                    <td style={{ padding: '0.5rem 0.75rem 0.5rem 0', fontFamily: 'monospace' }}>
                      {u.username}
                      {u.is_primary_sudo && (
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', opacity: 0.6 }}>(primary)</span>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', opacity: 0.7 }}>{u.email ?? '—'}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <select
                        value={u.storage_tier}
                        disabled={rowSaving[u.username]}
                        onChange={(e) => handleTierChange(u, e.target.value)}
                        style={{ fontSize: '0.8rem', width: 'auto' }}
                      >
                        <option value="rando">rando</option>
                        <option value="bud">bud</option>
                        <option value="bff">bff</option>
                      </select>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', opacity: 0.7 }}>
                      {formatBytes(u.storage_used / (1024 * 1024))}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={u.is_sudo}
                        disabled={u.is_primary_sudo || rowSaving[u.username]}
                        onChange={(e) => handleSudoChange(u, e.target.checked)}
                        title={u.is_primary_sudo ? 'cannot change primary sudo' : undefined}
                      />
                      {rowError[u.username] && (
                        <span style={{ marginLeft: '0.5rem', color: 'var(--color-1)', fontSize: '0.75rem' }}>
                          {rowError[u.username]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ marginBottom: '2.5rem' }}>
        <h3>server config</h3>
        <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0.25rem 0 0.75rem' }}>storage quotas in mb</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: 280 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            <span style={{ width: 60 }}>rando</span>
            <input
              type="number"
              min={1}
              value={randoMb}
              onChange={(e) => { setRandoMb(e.target.value); setConfigSaved(false) }}
              style={{ flex: 1 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            <span style={{ width: 60 }}>bud</span>
            <input
              type="number"
              min={1}
              value={budMb}
              onChange={(e) => { setBudMb(e.target.value); setConfigSaved(false) }}
              style={{ flex: 1 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            <span style={{ width: 60 }}>bff</span>
            <input
              type="number"
              min={1}
              value={bffMb}
              onChange={(e) => { setBffMb(e.target.value); setConfigSaved(false) }}
              style={{ flex: 1 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            <span style={{ width: 60 }}>default</span>
            <select
              value={defaultTier}
              onChange={(e) => { setDefaultTier(e.target.value); setConfigSaved(false) }}
              style={{ flex: 1, width: 'auto' }}
            >
              <option value="rando">rando</option>
              <option value="bud">bud</option>
              <option value="bff">bff</option>
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.75rem' }}>
          <button type="button" onClick={handleSaveConfig} disabled={configSaving} style={{ width: 'auto' }}>
            {configSaving ? 'saving…' : 'save config'}
          </button>
          {configSaved && <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>saved</span>}
          {configError && <span style={{ fontSize: '0.85rem', color: 'var(--color-1)' }}>{configError}</span>}
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>global theme</h3>
        <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0.25rem 0 0.75rem' }}>
          default colors shown to logged-out visitors
        </p>
        <div style={{ marginTop: '0.5rem' }}>
          {globalThemeLoaded && (
            <SchemePicker
              colorTheme={globalThemeRaw}
              onColorThemeChange={(t) => { setGlobalThemeRaw(t); setThemeSaved(false) }}
              username={username}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={handleSaveGlobalTheme}
            disabled={themeSaving || !globalThemeRaw}
            style={{ width: 'auto' }}
          >
            {themeSaving ? 'saving…' : 'save global theme'}
          </button>
          {themeSaved && <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>saved</span>}
          {themeError && <span style={{ fontSize: '0.85rem', color: 'var(--color-1)' }}>{themeError}</span>}
        </div>
      </section>
    </div>
  )
}
